import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, auth, getUserData, updateUserAiQuizCount } from '../firebase';
import { collection, addDoc, doc, writeBatch } from 'firebase/firestore';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CheckCircle, FilePdf, Spinner, Play, Circle, FloppyDisk } from "@phosphor-icons/react";

function AIQuiz() {
  const [title, setTitle] = useState('');
  const [baseData, setBaseData] = useState('');
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [questionCount, setQuestionCount] = useState(5);
  const [multipleChoiceRatio, setMultipleChoiceRatio] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState('');
  const [questions, setQuestions] = useState([]);
  const [aiQuizCount, setAiQuizCount] = useState(5);
  const [customApiKey, setCustomApiKey] = useState(null);
  const [gptModel, setGptModel] = useState('gpt-4o-mini');
  const [userPrompt, setUserPrompt] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      if (auth.currentUser) {
        const userData = await getUserData(auth.currentUser.uid);
        setAiQuizCount(userData.aiQuizCount);
        setCustomApiKey(userData.customApiKey);
        if (userData.customApiKey) {
          setGptModel(userData.gptModel || 'gpt-4o-mini');
        }
      }
    };
    fetchUserData();
  }, []);

  const handleFileUpload = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setFileName(selectedFile.name);
    }
  };

  const generateQuiz = async () => {
    if (aiQuizCount <= 0 && !customApiKey) {
      alert('AI 퀴즈 생성 횟수를 모두 사용하셨습니다.');
      return;
    }

    if (!title.trim()) {
      alert('퀴즈 제목을 입력해주세요.');
      return;
    }

    setIsLoading(true);
    setLoadingStatus('준비 중...');

    let uploadedFileId = null;
    let vectorStoreId = null;
    let assistantId = null;

    try {
      const apiKey = customApiKey || process.env.REACT_APP_OPENAI_API_KEY;
      const headers = {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
        'OpenAI-Beta': 'assistants=v2'
      };

      // 1. Upload File (if exists)
      if (file) {
        setLoadingStatus('파일 분석 중...');
        const formData = new FormData();
        formData.append('file', file);
        formData.append('purpose', 'assistants');

        const uploadResponse = await axios.post('https://api.openai.com/v1/files', formData, {
          headers: {
            'Authorization': `Bearer ${apiKey}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        uploadedFileId = uploadResponse.data.id;

        // 2. Create Vector Store
        setLoadingStatus('파일 분석 중...');
        const vectorStoreResponse = await axios.post('https://api.openai.com/v1/vector_stores', {
          name: `Quiz_${Date.now()}`,
          file_ids: [uploadedFileId]
        }, { headers });
        vectorStoreId = vectorStoreResponse.data.id;

        // Wait for vector store to be ready
        setLoadingStatus('문항 인덱싱 중...');
        let vsStatus = 'in_progress';
        while (vsStatus === 'in_progress') {
          await new Promise(r => setTimeout(r, 1000));
          const vsStatusResponse = await axios.get(`https://api.openai.com/v1/vector_stores/${vectorStoreId}`, { headers });
          vsStatus = vsStatusResponse.data.status;
          if (vsStatus === 'failed') {
            throw new Error('Vector Store 생성 실패');
          }
        }
        console.log("✅ Vector Store 준비 완료:", vectorStoreId);
      }

      // 3. Create Assistant with Vector Store
      setLoadingStatus('문항 인덱싱 중...');
      const assistantPayload = {
        name: "Quiz Generator",
        instructions: "You are a helpful assistant that generates quizzes. You MUST use the 'file_search' tool to analyze uploaded files and generate questions based ONLY on that content.",
        model: gptModel,
        tools: [{ type: "file_search" }],
        response_format: { type: "json_object" }
      };

      // Add Vector Store to Assistant if exists
      if (vectorStoreId) {
        assistantPayload.tool_resources = {
          file_search: {
            vector_store_ids: [vectorStoreId]
          }
        };
      }

      const assistantResponse = await axios.post('https://api.openai.com/v1/assistants', assistantPayload, { headers });
      assistantId = assistantResponse.data.id;

      // 4. Create Thread
      const threadResponse = await axios.post('https://api.openai.com/v1/threads', {}, { headers });
      const threadId = threadResponse.data.id;

      // 5. Add Message
      setLoadingStatus('퀴즈 초안 생성 중...');

      // 정확한 선다형/단답형 개수 계산
      const multipleChoiceCount = Math.round(questionCount * multipleChoiceRatio / 100);
      const shortAnswerCount = questionCount - multipleChoiceCount;

      const prompt = `
        "${title}"이라는 제목으로 문제와 답변을 만드세요.
        
        [필수 요구사항 - 반드시 지켜야 함]
        - 총 문항 수: 정확히 ${questionCount}개 (절대로 더 많거나 적게 생성하지 마세요)
        - 선다형 문항: 정확히 ${multipleChoiceCount}개
        - 단답형 문항: 정확히 ${shortAnswerCount}개
        - 위 개수를 반드시 정확히 지켜주세요. 개수가 맞지 않으면 응답이 거부됩니다.
        
        ${file ? `
[중요] PDF 파일이 업로드되었습니다.
- 반드시 'file_search' 도구를 사용하여 첨부된 PDF 파일의 내용을 정밀하게 분석하세요.
- 모든 문제는 PDF 파일에 포함된 내용에만 기반하여 출제해야 합니다.
- PDF 파일에 없는 내용이나 외부 지식으로부터 문제를 생성하지 마세요.
- PDF 파일의 핵심 개념, 용어, 사실을 중심으로 문제를 구성하세요.
` : ""}
        
        ${baseData ? `추가 참고 텍스트: ${baseData}` : ''}

        ${userPrompt ? `[사용자 추가 요청사항]: ${userPrompt}\n위 사용자의 추가 요청사항을 최대한 반영하여 퀴즈를 생성하세요.\n` : ""}

        단답형 문제의 경우:
        - 답변은 무조건 한 단어로만 만들어져야 합니다.
        - (    ) 혹은 ______ 같은 빈칸 채우기 문제도 좋습니다.
        
        선다형 문제의 경우:
        - 4개의 보기를 제공해야 합니다.
        - 정답은 하나만 있어야 합니다.
        - 오답은 그럴듯하면서도 명확히 구분되어야 합니다.
        
        각 문제는 다음 형식의 객체로 응답해주세요:
        단답형: { "question": "문제", "answer": "답변", "isMultipleChoice": false }
        선다형: { "question": "문제", "answer": "정답", "isMultipleChoice": true, "answerOptions": ["보기1", "보기2", "보기3", "보기4"], "correctAnswers": [정답인덱스] }
        
        전체 응답은 이러한 객체들의 배열 형태로 제공해주세요. 
        순수한 JSON 형식으로만 응답해주세요.
        
        다시 한번 강조: 선다형 ${multipleChoiceCount}개, 단답형 ${shortAnswerCount}개, 총 ${questionCount}개를 정확히 생성하세요.
      `;

      await axios.post(`https://api.openai.com/v1/threads/${threadId}/messages`, {
        role: "user",
        content: prompt
      }, { headers });

      // 6. Run
      setLoadingStatus('퀴즈 생성 중... (시간이 조금 걸릴 수 있습니다)');
      const runPayload = {
        assistant_id: assistantId
      };

      // Force file_search tool usage if file exists
      if (file && vectorStoreId) {
        runPayload.tool_choice = { type: "file_search" };
      }

      const runResponse = await axios.post(`https://api.openai.com/v1/threads/${threadId}/runs`, runPayload, { headers });
      const runId = runResponse.data.id;

      // 7. Poll
      let status = 'queued';
      while (status !== 'completed' && status !== 'failed' && status !== 'cancelled') {
        await new Promise(r => setTimeout(r, 1000));
        const statusResponse = await axios.get(`https://api.openai.com/v1/threads/${threadId}/runs/${runId}`, { headers });
        status = statusResponse.data.status;
        if (status === 'failed' || status === 'cancelled') {
          throw new Error(statusResponse.data.last_error?.message || 'Run failed');
        }
      }

      // 8. Verify File Search Usage
      if (file) {
        try {
          const stepsResponse = await axios.get(`https://api.openai.com/v1/threads/${threadId}/runs/${runId}/steps`, { headers });
          const runSteps = stepsResponse.data.data;
          const hasFileSearch = runSteps.some(step => step.step_details?.tool_calls?.some(tool => tool.type === 'file_search'));

          if (hasFileSearch) {
            console.log("✅ File Search 도구가 성공적으로 사용되었습니다.");
          } else {
            console.warn("⚠️ File Search 도구가 사용되지 않았습니다.");
          }
        } catch (stepError) {
          console.warn("Run steps 확인 중 오류:", stepError);
        }
      }

      // 9. Get Messages
      const messagesResponse = await axios.get(`https://api.openai.com/v1/threads/${threadId}/messages`, { headers });
      const lastMessage = messagesResponse.data.data[0];
      const content = lastMessage.content[0].text.value;

      let generatedQuestions;
      try {
        generatedQuestions = JSON.parse(content);
        // Handle case where AI wraps it in an object like { "questions": [...] }
        if (!Array.isArray(generatedQuestions) && generatedQuestions.questions) {
          generatedQuestions = generatedQuestions.questions;
        }
      } catch (parseError) {
        console.error('JSON Parsing Error:', parseError);
        // Try to extract JSON from markdown code blocks if present
        const jsonMatch = content.match(/```json\n([\s\S]*?)\n```/) || content.match(/\[[\s\S]*\]/);
        if (jsonMatch) {
          generatedQuestions = JSON.parse(jsonMatch[1] || jsonMatch[0]);
        } else {
          throw new Error('유효한 JSON을 찾을 수 없습니다.');
        }
      }

      // 생성된 문항 검증 및 조정
      const expectedMCCount = Math.round(questionCount * multipleChoiceRatio / 100);
      const expectedSACount = questionCount - expectedMCCount;

      // 문항 수 초과 시 자르기
      if (generatedQuestions.length > questionCount) {
        console.log(`⚠️ 생성된 문항 수(${generatedQuestions.length}개)가 요청한 수(${questionCount}개)보다 많아 자릅니다.`);
        generatedQuestions = generatedQuestions.slice(0, questionCount);
      }

      // 현재 선다형/단답형 개수 확인
      const actualMCCount = generatedQuestions.filter(q => q.isMultipleChoice).length;
      const actualSACount = generatedQuestions.filter(q => !q.isMultipleChoice).length;

      console.log(`📊 문항 생성 결과:`);
      console.log(`   - 총 문항 수: ${generatedQuestions.length}개 (기대값: ${questionCount}개)`);
      console.log(`   - 선다형: ${actualMCCount}개 (기대값: ${expectedMCCount}개)`);
      console.log(`   - 단답형: ${actualSACount}개 (기대값: ${expectedSACount}개)`);

      // 문항 수 부족 시 경고
      if (generatedQuestions.length < questionCount) {
        console.warn(`⚠️ 생성된 문항 수(${generatedQuestions.length}개)가 요청한 수(${questionCount}개)보다 적습니다.`);
      }

      // 비율이 크게 다른 경우 경고 (허용 오차: 1개)
      if (Math.abs(actualMCCount - expectedMCCount) > 1) {
        console.warn(`⚠️ 선다형 비율이 기대값과 다릅니다. (실제: ${actualMCCount}개, 기대: ${expectedMCCount}개)`);
      }

      if (!customApiKey) {
        const newCount = aiQuizCount - 1;
        await updateUserAiQuizCount(auth.currentUser.uid, newCount);
        setAiQuizCount(newCount);
      }

      setQuestions(generatedQuestions);

    } catch (error) {
      console.error('퀴즈 생성 오류:', error);
      alert(`퀴즈 생성 중 오류가 발생했습니다: ${error.message}`);
    } finally {
      // Cleanup: Delete Vector Store, Assistant, and File
      const apiKey = customApiKey || process.env.REACT_APP_OPENAI_API_KEY;
      const headers = {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
        'OpenAI-Beta': 'assistants=v2'
      };

      if (assistantId) {
        try {
          await axios.delete(`https://api.openai.com/v1/assistants/${assistantId}`, { headers });
          console.log("✅ Assistant 삭제 완료");
        } catch (cleanupError) {
          console.warn("Assistant 삭제 실패:", cleanupError);
        }
      }

      if (vectorStoreId) {
        try {
          await axios.delete(`https://api.openai.com/v1/vector_stores/${vectorStoreId}`, { headers });
          console.log("✅ Vector Store 삭제 완료");
        } catch (cleanupError) {
          console.warn("Vector Store 삭제 실패:", cleanupError);
        }
      }

      if (uploadedFileId) {
        try {
          await axios.delete(`https://api.openai.com/v1/files/${uploadedFileId}`, { headers });
          console.log("✅ File 삭제 완료");
        } catch (cleanupError) {
          console.warn("File 삭제 실패:", cleanupError);
        }
      }

      setIsLoading(false);
      setLoadingStatus('');
    }
  };

  const saveQuiz = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        alert('로그인이 필요합니다.');
        return;
      }

      const uniqueCode = Math.random().toString(36).substring(2, 8).toUpperCase();
      const quizRef = await addDoc(collection(db, 'quizzes'), {
        name: title,
        userId: user.uid,
        createdAt: new Date(),
        code: uniqueCode,
        isAIGenerated: true
      });

      const batch = writeBatch(db);
      questions.forEach((question, index) => {
        const questionRef = doc(db, `quizzes/${quizRef.id}/questions`, index.toString());
        batch.set(questionRef, {
          question: question.question,
          answer: question.answer,
          isMultipleChoice: question.isMultipleChoice,
          answerOptions: question.answerOptions || [question.answer],
          correctAnswers: question.correctAnswers || [0],
          order: index,
        });
      });

      await batch.commit();
      alert('퀴즈가 성공적으로 저장되었습니다.');
      navigate('/teacher-dashboard');
    } catch (error) {
      console.error('Error saving quiz:', error);
      alert('저장 중 오류가 발생했습니다.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-100 via-pink-50 to-green-50 flex flex-col items-center justify-center px-4 pt-32 pb-40">
      <Card className="w-full max-w-3xl shadow-2xl border-0 bg-white/90 backdrop-blur-sm">
        <CardHeader className="pb-4 border-b border-gray-100">
          <CardTitle className="text-base font-bold text-gray-500">*AI 퀴즈 생성</CardTitle>
        </CardHeader>

        <CardContent className="space-y-8 pt-8">
          {/* Settings Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>퀴즈 제목</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="퀴즈 제목을 입력하세요"
                className="bg-gray-50 border-gray-200 focus:bg-white transition-colors"
              />
            </div>
            <div className="space-y-2">
              <Label>문제 개수 ({questionCount}개)</Label>
              <Input
                type="number"
                value={questionCount}
                onChange={(e) => setQuestionCount(parseInt(e.target.value))}
                min="1" max="20"
                className="bg-gray-50 border-gray-200 focus:bg-white transition-colors"
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label>선다형 문제 비율</Label>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">{multipleChoiceRatio}%</span>
                <span className="text-sm font-medium px-2 py-0.5 bg-primary/10 text-primary rounded-full">
                  선다형 {Math.round(questionCount * multipleChoiceRatio / 100)}개 / 단답형 {questionCount - Math.round(questionCount * multipleChoiceRatio / 100)}개
                </span>
              </div>
            </div>
            <input
              type="range"
              value={multipleChoiceRatio}
              onChange={(e) => setMultipleChoiceRatio(parseInt(e.target.value))}
              min="0" max="100" step="10"
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
            />
          </div>

          {/* Upload Area */}
          <div
            className="group border-2 border-dashed border-gray-200 hover:border-primary/50 rounded-2xl p-10 flex flex-col items-center justify-center text-center hover:bg-blue-50/30 transition-all cursor-pointer"
            onClick={() => document.getElementById('file-upload').click()}
          >
            <input
              id="file-upload"
              type="file"
              accept=".pdf,.txt,.doc,.docx"
              className="hidden"
              onChange={handleFileUpload}
            />
            <div className="bg-white p-4 rounded-xl shadow-sm mb-4 group-hover:scale-110 transition-transform duration-300">
              <FilePdf size={32} className="text-primary" weight="duotone" />
            </div>
            <h3 className="text-lg font-semibold text-gray-700 group-hover:text-primary transition-colors">
              {fileName || "PDF 파일 업로드"}
            </h3>
            <p className="text-sm text-gray-400 mt-1">
              {fileName ? "파일이 선택되었습니다" : "클릭하거나 파일을 드롭하세요"}
            </p>
          </div>

          {/* Text Input Area */}
          <div className="space-y-2">
            <Label>추가 텍스트 데이터 (선택)</Label>
            <textarea
              value={baseData}
              onChange={(e) => setBaseData(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 min-h-[100px] text-sm"
              placeholder="PDF 외에 추가로 참고할 텍스트가 있다면 입력하세요."
            />
          </div>

          {/* User Prompt Input Area */}
          <div className="space-y-2">
            <Label>AI 추가 요청사항 (선택)</Label>
            <Input
              value={userPrompt}
              onChange={(e) => setUserPrompt(e.target.value)}
              placeholder="예: 초등학생 수준으로 만들어줘, 난이도를 상/중/하로 섞어줘"
              className="bg-gray-50 border-gray-200 focus:bg-white transition-colors"
            />
          </div>

          {/* Action Button */}
          <Button
            onClick={generateQuiz}
            disabled={isLoading}
            className="w-full h-14 text-lg font-bold shadow-lg shadow-primary/20 hover:shadow-primary/30 transition-all"
          >
            {isLoading ? (
              <>
                <Spinner className="animate-spin mr-2" size={24} />
                {loadingStatus || '준비 중...'}
              </>
            ) : (
              <>
                <Play size={24} className="mr-2" weight="fill" />
                출제 시작하기
              </>
            )}
          </Button>

          {/* Footer Status */}
          <div className="flex justify-between items-center pt-2">
            <div className="flex items-center space-x-2 px-3 py-1 bg-green-50 rounded-full border border-green-100">
              <Circle size={12} weight="fill" className="text-green-400" />
              <span className="text-xs font-bold text-green-600">OPENAI · {gptModel}</span>
            </div>
            <div className="flex items-center space-x-2 px-3 py-1 bg-green-50 rounded-full border border-green-100">
              <CheckCircle size={16} weight="fill" className="text-green-500" />
              <span className="text-xs font-bold text-green-600">
                {customApiKey ? "API 키 설정됨" : `남은 횟수: ${aiQuizCount}`}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results Section */}
      {questions.length > 0 && (
        <Card className="w-full max-w-3xl mt-8 shadow-xl border-0 bg-white/90 backdrop-blur-sm animate-accordion-down">
          <CardHeader>
            <CardTitle className="text-primary">생성된 퀴즈 결과</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {questions.map((q, index) => (
              <div key={index} className="p-6 bg-gray-50 rounded-xl border border-gray-100 hover:border-primary/20 transition-colors">
                <div className="flex items-start">
                  <span className="flex-shrink-0 w-8 h-8 bg-primary/10 text-primary rounded-full flex items-center justify-center font-bold mr-3 text-sm">
                    Q{index + 1}
                  </span>
                  <div className="flex-grow">
                    <p className="font-bold text-gray-800 mb-3 text-lg">{q.question}</p>
                    {q.isMultipleChoice ? (
                      <div className="grid grid-cols-1 gap-2">
                        {q.answerOptions.map((option, optIndex) => (
                          <div
                            key={optIndex}
                            className={`p-3 rounded-lg border ${q.correctAnswers.includes(optIndex)
                              ? 'bg-green-50 border-green-200 text-green-700'
                              : 'bg-white border-gray-200 text-gray-600'
                              }`}
                          >
                            <span className="font-medium mr-2">{optIndex + 1}.</span>
                            {option}
                            {q.correctAnswers.includes(optIndex) && <CheckCircle className="inline ml-2 text-green-500" weight="fill" />}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="p-4 bg-blue-50 rounded-lg text-blue-800 font-medium">
                        A: {q.answer}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            <Button onClick={saveQuiz} className="w-full h-12" variant="secondary">
              <FloppyDisk size={20} className="mr-2" />
              퀴즈 저장하기
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default AIQuiz;